/**
 * Splyt Brand Design Tokens
 * Canonical source: ../../../splyt-design-system/tokens.json (see its SOURCE_OF_TRUTH.md).
 * To change a value: edit tokens.json there → `node build-tokens.mjs` → reconcile
 * dist/brand.tokens.js into this file. Never invent token values directly here.
 * (Historical origin: design_handoff_invite_web_flow/source/splyt.css, May 2026.)
 *
 * Brand tokens for the invite web flow. Isolated from the existing
 * src/style/colors.js (LEGACY — old pages only) to avoid breaking existing pages.
 */

// ============ BRAND COLORS ============

// Blueberry — primary (purple)
export const blueberry = '#574CFF';
export const blueberry75 = '#8078FF';
export const blueberry50 = '#ABA5FF';
export const blueberry25 = '#D5D2FF';
export const blueberry10 = '#D5D2FF1A';

// Mustard — secondary (yellow)
export const mustard = '#EFFD5A';
export const mustard75 = '#F3FD84';
export const mustard50 = '#F7FEAC';
export const mustard25 = '#FBFED5';

// Bubble Gum — tertiary (magenta/pink)
export const bubblegum = '#FF8FE9';
export const bubblegum75 = '#FFABEE';
export const bubblegum50 = '#FFC7F4';
export const bubblegum25 = '#FFE3F9';

// Ketchup — error/warning (orange-red)
export const ketchup = '#FE5C2B';
export const ketchup75 = '#FE8560';
export const ketchup50 = '#FFAD95';
export const ketchup25 = '#FFD6CA';

// Positive — success (deeper, AA-accessible green — canon since 2026-06-28)
export const positive = '#1F8A5B';
export const positiveTint = '#E2F5EB';

// Marshmallow — light neutrals / backgrounds (warm cream)
export const marshmallow = '#FAF6F0';
export const marshmallow75 = '#FBF8F4';
export const marshmallow50 = '#FDFBF8';
export const marshmallow25 = '#FEFDFB';
export const marshmallowNeutral = '#EEEDE9';

// Caviar — dark neutrals / text
export const caviar = '#191611';
export const caviar75 = '#534D44';
export const caviar50 = '#8C8478';
export const caviar25 = '#C5C1BB';
export const caviar10 = '#EFEEEC';

// Surfaces
export const surface = '#FFFFFF';

// Web-local convenience: canon replacement for the legacy `primaryBorder`
export const primaryBorder = `1px solid ${marshmallowNeutral}`;

// ============ GUEST PALETTE ============
// 12 saturated-pastel colors assigned to guests by index
export const guestColors = [
  '#FF6B6B', // guest-1
  '#4ECDC4', // guest-2
  '#45B7D1', // guest-3
  '#96CEB4', // guest-4
  '#FFEAA7', // guest-5
  '#DDA0DD', // guest-6
  '#98D8C8', // guest-7
  '#F7DC6F', // guest-8
  '#BB8FCE', // guest-9
  '#85C1E9', // guest-10
  '#F8B500', // guest-11
  '#82E0AA', // guest-12
];

// ============ TYPOGRAPHY ============
export const fontSans = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"SF Pro Text"',
  '"SF Pro Display"',
  'Inter',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(', ');

export const fontMono = [
  'ui-monospace',
  '"SF Mono"',
  'Menlo',
  'Monaco',
  '"Cascadia Mono"',
  'monospace',
].join(', ');

// Type scale (for reference — use in makeStyles)
export const typeScale = {
  displayLg: { fontWeight: 700, fontSize: 57, lineHeight: 1.05 },
  displayMd: { fontWeight: 700, fontSize: 45, lineHeight: 1.08 },
  displaySm: { fontWeight: 700, fontSize: 36, lineHeight: 1.12 },
  headlineLg: { fontWeight: 700, fontSize: 32, lineHeight: 1.15 },
  headlineMd: { fontWeight: 700, fontSize: 28, lineHeight: 1.2 },
  headlineSm: { fontWeight: 600, fontSize: 24, lineHeight: 1.25 },
  titleLg: { fontWeight: 600, fontSize: 22, lineHeight: 1.3 },
  titleMd: { fontWeight: 600, fontSize: 16, lineHeight: 1.35 },
  titleSm: { fontWeight: 600, fontSize: 14, lineHeight: 1.4 },
  bodyLg: { fontWeight: 400, fontSize: 16, lineHeight: 1.5 },
  bodyMd: { fontWeight: 400, fontSize: 14, lineHeight: 1.5 },
  bodySm: { fontWeight: 400, fontSize: 12, lineHeight: 1.45 },
  labelLg: { fontWeight: 600, fontSize: 14, lineHeight: 1.2 },
  labelMd: { fontWeight: 500, fontSize: 12, lineHeight: 1.2 },
  labelSm: { fontWeight: 500, fontSize: 11, lineHeight: 1.2 },
};

// ============ SPACING (8pt grid) ============
export const spacing = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

// ============ RADII ============
export const radii = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 999,
  // Specific use cases from design spec
  input: 14,
  card: 18, // canon since 2026-06-28 — matches the live mobile app (was 28)
  memberTile: 16,
  groupAvatar: (size) => size * 0.28,
};

// ============ ELEVATION / SHADOWS ============
export const shadows = {
  elev1: '0 1px 2px rgba(0,0,0,0.05)',
  elev2: '0 2px 4px rgba(0,0,0,0.08)',
  elev3: '0 3px 6px rgba(0,0,0,0.12)',
  elev4: '0 4px 8px rgba(0,0,0,0.15)',
  elev5: '0 5px 10px rgba(0,0,0,0.18)',
  // Component-specific
  primaryButton: '0 1px 0 rgba(0,0,0,0.18), 0 6px 16px rgba(25,22,17,0.18)',
  card: '0 1px 0 rgba(0,0,0,0.04), 0 24px 60px rgba(25,22,17,0.08), 0 4px 12px rgba(25,22,17,0.04)',
  inputFocus: '0 0 0 4px rgba(25,22,17,0.06)',
  groupAvatar: (accent) => `0 14px 36px ${accent}55, 0 2px 4px rgba(0,0,0,0.06)`,
};

// ============ BREAKPOINTS ============
export const breakpoints = {
  mobile: 640,
  desktop: 641,
};

// ============ COMPONENT TOKENS ============
// Ready-to-use style objects for common patterns

export const componentStyles = {
  primaryButton: {
    background: caviar,
    color: surface,
    borderRadius: radii.pill,
    padding: '16px 22px',
    fontWeight: 700,
    fontSize: 16,
    boxShadow: shadows.primaryButton,
    border: 'none',
    cursor: 'pointer',
    letterSpacing: '-0.005em',
  },
  ghostButton: {
    background: 'transparent',
    color: caviar,
    borderRadius: radii.pill,
    padding: '15px 22px',
    fontWeight: 600,
    fontSize: 16,
    border: `1.5px solid ${caviar25}`,
    cursor: 'pointer',
  },
  textLink: {
    color: blueberry,
    fontWeight: 600,
    fontSize: 14,
    textDecoration: 'none',
    borderBottom: `1.5px solid ${blueberry25}`,
    paddingBottom: 1,
    cursor: 'pointer',
  },
  inputWell: {
    background: surface,
    borderRadius: radii.input,
    border: `1.5px solid ${marshmallowNeutral}`,
    padding: '14px 16px',
  },
  inputWellFocused: {
    border: `1.5px solid ${caviar}`,
    boxShadow: shadows.inputFocus,
  },
  card: {
    background: surface,
    borderRadius: radii.card,
    border: `1px solid ${marshmallowNeutral}`,
    boxShadow: shadows.card,
  },
  memberPill: {
    background: marshmallow75,
    borderRadius: radii.pill,
    border: `1px solid ${marshmallowNeutral}`,
    padding: '8px 16px 8px 10px',
  },
};

// ============ CONVENIENCE EXPORT ============
// Matches the IW object from the prototype for easier reference
export const brand = {
  caviar,
  caviar75,
  caviar50,
  caviar25,
  marsh: marshmallow,
  marsh75: marshmallow75,
  marshNeutral: marshmallowNeutral,
  surface,
  blueberry,
  blueberry25,
  bubblegum,
  mustard,
  positive,
  ketchup,
};
